import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { ChecklistChanged } from '@/domain/events/ChecklistChanged'
import { SessionExpired } from '@/domain/events/SessionExpired'
import { Checklist } from '@/domain/models/Checklist'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import {
  ChecklistDetailResponse,
  ChecklistListResponse,
  ChecklistWirePojo,
  findChecklistWireLimitViolation,
  toChecklistWirePojo
} from './checklistWire'
import { IChecklistRepository } from './IChecklistRepository'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import type { HttpOptions, HttpResponse, IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import { isRecord, parseDateOrFallback, toNonEmptyTrimmedString } from '@/utils/safe'

// The pilotapp uses 30 s for the same resource: `procedures` reaches tens of KB per checklist.
// Error keys of the API, as documented in the backend integration guide. `message` is a key, never
// text for the user: whoever renders it maps it to an i18n token.
const FORBIDDEN_RESOURCE = 'Forbidden resource'
const CANNOT_UPDATE_DELETED_CHECKLIST = 'cannotUpdateDeletedChecklist'

const CHECKLIST_INVALID = 'CHECKLIST_INVALID'
const CHECKLIST_NOT_FOUND = 'CHECKLIST_NOT_FOUND'
const CHECKLIST_REQUEST_FAILED = 'CHECKLIST_REQUEST_FAILED'
const CHECKLIST_SESSION_EXPIRED = 'CHECKLIST_SESSION_EXPIRED'
const CHECKLIST_INVALID_REQUEST = 'CHECKLIST_INVALID_REQUEST'

@injectable()
export class ChecklistRepository implements IChecklistRepository {
  // Authoritative in-memory cache: once loaded it holds ALL of the user's checklists, so reads never
  // hit the network again and save/delete keep it in place. `undefined` = never loaded; an array
  // (even empty) = loaded. Online-first by project decision: nothing is persisted to disk.
  private checklists: Checklist[]
  // Dedupes concurrent callers (init + first screen open) onto a single request.
  private loadPromise: Promise<Result<Checklist[]>> | null = null

  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    // Same-origin via proxy (Vite em dev, nginx em producao), como todas as outras chamadas da API
    // principal. Reduz a exposicao ao `ERR_NETWORK_CHANGED` e elimina o preflight `OPTIONS`.
    return `${NEXATLAS_API_BASE}/userChecklist`
  }

  async init(): Promise<void> {
    await this.ensureLoaded()
  }

  async getChecklists(forceRefresh = false): Promise<Result<Checklist[]>> {
    return this.ensureLoaded(forceRefresh)
  }

  async getChecklist(id: UniqueEntityID, forceRefresh = false): Promise<Result<Checklist>> {
    const checklistId = toNonEmptyTrimmedString(id?.toString())
    if (!checklistId) return Result.fail(CHECKLIST_INVALID)

    const loadResult = await this.ensureLoaded(forceRefresh)
    if (loadResult.isSuccess) {
      const foundChecklist = loadResult.getValue().find((checklist) => checklist.id.equals(id))
      if (foundChecklist) return Result.ok(foundChecklist)
    }

    // Remote fallback: the checklist may be outside the cache (a link to a checklist created on
    // another device after this session loaded, or a load that failed). The cache is left untouched
    // here — a single record is not a snapshot.
    try {
      const response = await this._send<ChecklistDetailResponse>(() =>
        this.httpClient.get(`${this.getRemoteUrl()}/${checklistId}`, this._requestOptions())
      )

      if (!this._isSuccessful(response)) {
        return Result.fail(response.status === 404 ? CHECKLIST_NOT_FOUND : this._failureKeyOf(response))
      }

      const wirePojo = response.response?.checklist
      if (!isRecord(wirePojo)) return Result.fail(CHECKLIST_NOT_FOUND)

      return Result.ok(this._fromWirePojo(wirePojo))
    } catch (error) {
      console.error('Error in getChecklist:', error)
      return Result.fail(`Error fetching checklist: ${error}`)
    }
  }

  async saveChecklist(checklist: Checklist): Promise<Result<void>> {
    if (!checklist) return Result.fail(CHECKLIST_INVALID)

    checklist.updatedAt = new Date()

    const wirePojo = toChecklistWirePojo(checklist)
    // Guards come before the request on purpose: past the column limits the API answers `500`, and a
    // 500 gives the user nothing to act on.
    const limitViolation = findChecklistWireLimitViolation(wirePojo)
    if (limitViolation) return Result.fail(limitViolation)

    try {
      const response = await this._send(() =>
        this.httpClient.post(this.getRemoteUrl(), wirePojo, this._requestOptions(true))
      )

      if (!this._isSuccessful(response)) {
        const failureKey = this._failureKeyOf(response)

        // The record was soft-deleted elsewhere and the server refuses to resurrect it. Drop the
        // stale entry so the screens stop offering it; reviving requires a brand new `id`.
        if (failureKey === CANNOT_UPDATE_DELETED_CHECKLIST) {
          this._removeFromCache(checklist.id)
          ChecklistChanged.next()
        }

        return Result.fail(failureKey)
      }

      // The saved entity — not a server echo — is what lands in the cache: `POST` answers only
      // `{success:true}`, so the server's `updatedAt` would only arrive on a later `GET`. The local
      // stamp is optimistic, and good enough to order the list.
      // It goes in through the wire, though, as a `GET` would bring it: the procedure ids are local and
      // get regenerated by position, and the items step has them in its URL. Caching the editor's ids
      // would let a reload after a reorder open a different procedure under the same URL (PLA-286).
      this._upsertInCache(this._fromWirePojo(wirePojo))
      ChecklistChanged.next()

      return Result.ok()
    } catch (error) {
      console.error('Error in saveChecklist:', error)
      return Result.fail(`Error saving checklist: ${error}`)
    }
  }

  async deleteChecklist(id: UniqueEntityID): Promise<Result<void>> {
    const checklistId = toNonEmptyTrimmedString(id?.toString())
    if (!checklistId) return Result.fail(CHECKLIST_INVALID)

    try {
      const response = await this._send(() =>
        this.httpClient.delete(`${this.getRemoteUrl()}/${checklistId}`, {}, this._requestOptions(true))
      )

      // `DELETE` is not idempotent: a retried delete answers `404 invalidChecklistId`. The record is
      // gone either way, so for the caller that is success, not an error.
      const isAlreadyGone = response.success !== false && response.status === 404
      if (!this._isSuccessful(response) && !isAlreadyGone) return Result.fail(this._failureKeyOf(response))

      this._removeFromCache(id)
      ChecklistChanged.next()

      return Result.ok()
    } catch (error) {
      console.error('Error in deleteChecklist:', error)
      return Result.fail(`Error deleting checklist: ${error}`)
    }
  }

  // PRIVATE METHODS

  private async ensureLoaded(forceRefresh = false): Promise<Result<Checklist[]>> {
    if (this.checklists !== undefined && !forceRefresh) return Result.ok(this.checklists)

    // A forced refresh joins a load already in flight instead of opening a second one: the list
    // screen asks for the cache and for a revalidation in the same tick, and with a cold cache both
    // want the very same request.
    if (!this.loadPromise) {
      this.loadPromise = this._getRemoteChecklists()
    }

    try {
      return await this.loadPromise
    } finally {
      // Cleared on success and on failure alike, so a failed load doesn't pin an error for the
      // whole session and the next caller gets a fresh attempt.
      this.loadPromise = null
    }
  }

  private async _getRemoteChecklists(): Promise<Result<Checklist[]>> {
    try {
      const response = await this._send<ChecklistListResponse>(() =>
        this.httpClient.get(this.getRemoteUrl(), this._requestOptions())
      )

      if (!this._isSuccessful(response)) return Result.fail(this._failureKeyOf(response))

      const payload = response.response
      const wireChecklists = Array.isArray(payload?.checklists) ? payload.checklists : []

      // The cache is only replaced once the server has answered, so a failed load leaves it intact.
      // The route already filters out deleted records; the extra check costs nothing.
      this.checklists = wireChecklists
        .filter((wirePojo) => isRecord(wirePojo) && !wirePojo.deletedAt)
        .map((wirePojo) => this._fromWirePojo(wirePojo))

      return Result.ok(this.checklists)
    } catch (error) {
      console.error('Error in getChecklists:', error)
      return Result.fail(`Error fetching checklists: ${error}`)
    }
  }

  /**
   * @param idempotent marque `true` quando repetir a chamada nao muda o resultado no servidor. O
   * `AxiosHttpClient` so retenta escrita com esta flag, justamente para nao duplicar efeito.
   */
  private _requestOptions(idempotent = false): HttpOptions {
    return {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      },
      timeout: HTTP_TIMEOUTS.sync,
      idempotent
    }
  }

  /*
   * `_send` NAO retenta mais.
   *
   * Ele envolvia cada chamada em `withNetworkRetry` (6 tentativas) e re-lancava o erro transiente que
   * o `AxiosHttpClient` engolia. Isso fazia sentido antes da camada de confiabilidade do transporte;
   * hoje o proprio cliente HTTP ja retenta flap de rede (3 tentativas, com jitter), e as duas camadas
   * se MULTIPLICAVAM: medi **18 requisicoes HTTP reais** para um unico `getChecklists()` sob flap
   * persistente. O dono do retry de transporte e um so, o `AxiosHttpClient`.
   *
   * As escritas passam `idempotent: true` (o POST e upsert pelo `id` gerado no cliente e o DELETE e
   * por `id`, entao repetir nao duplica), o que devolve o retry a elas pelo caminho unico.
   */
  private async _send<T>(perform: () => Promise<HttpResponse<T>>): Promise<HttpResponse<T>> {
    return perform()
  }

  // `POST` answers 201 and `GET`/`DELETE` answer 200, so the whole 2xx range counts as success.
  private _isSuccessful<T>(response: HttpResponse<T>): response is Extract<HttpResponse<T>, { success: true }> {
    if (response.success === false) return false

    return response.status >= 200 && response.status < 300
  }

  /**
   * Maps a rejected answer to the error key the callers branch on.
   *
   * This API answers `403 Forbidden resource` — not `401` — for a missing or dead token, and
   * `AxiosHttpClient` only tries to recover the session on `401`. So the recovery never fires here
   * and the event has to be emitted from this layer, distinguishing it from the `403`s that are
   * about the resource (`notUsersChecklist`, `notUsersAircraft`).
   */
  private _failureKeyOf(response: HttpResponse<unknown>): string {
    if (response.success === false) return response.error?.message || CHECKLIST_REQUEST_FAILED

    const apiMessage = this._apiMessageOf(response.response)

    if (response.status === 403 && apiMessage === FORBIDDEN_RESOURCE) {
      SessionExpired.next()
      return CHECKLIST_SESSION_EXPIRED
    }

    return apiMessage || CHECKLIST_REQUEST_FAILED
  }

  private _apiMessageOf(body: unknown): string | null {
    if (!isRecord(body)) return null

    // A `400` with an array of messages is a contract bug (Nest validation), not something to show.
    if (Array.isArray(body.message)) return CHECKLIST_INVALID_REQUEST

    return toNonEmptyTrimmedString(body.message)
  }

  private _fromWirePojo(wirePojo: ChecklistWirePojo): Checklist {
    const fallbackDate = new Date()

    // `fromPojo` never throws: it coerces every field and drops whatever doesn't parse.
    return Checklist.fromPojo({
      ...wirePojo,
      createdAt: parseDateOrFallback(wirePojo?.createdAt, fallbackDate),
      updatedAt: parseDateOrFallback(wirePojo?.updatedAt, fallbackDate)
    })
  }

  private _upsertInCache(checklist: Checklist): void {
    const otherChecklists = (this.checklists ?? []).filter((cached) => !cached.id.equals(checklist.id))
    this.checklists = [...otherChecklists, checklist]
  }

  private _removeFromCache(id: UniqueEntityID): void {
    if (this.checklists === undefined) return

    this.checklists = this.checklists.filter((checklist) => !checklist.id.equals(id))
  }
}
